import React, { useEffect, useState } from 'react';
import DataService, { SpellImage } from '../../../XXX/DataService';
import './ChampionSkillOrder.css'; // Reusing the same CSS file

interface SkillPriority {
  primary_slot: number;
  secondary_slot: number;
  tertiary_slot: number;
  games: number;
  win_rate_pct: number;
}

interface ChampionSkillPriorityProps {
  championName: string;
}

// Mapping skill numbers to readable labels
const skillSlotToLabel = (slot: number): string => {
  switch (slot) {
    case 1: return 'Q';
    case 2: return 'W';
    case 3: return 'E';
    case 4: return 'R';
    default: return '?';
  }
};

const ChampionSkillPriority: React.FC<ChampionSkillPriorityProps> = ({ championName }) => {
  const [loading, setLoading] = useState(true);
  const [skillPriority, setSkillPriority] = useState<SkillPriority[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [spellImages, setSpellImages] = useState<SpellImage[]>([]);
  const [spellImagesLoading, setSpellImagesLoading] = useState(true);

  useEffect(() => {
    const fetchSpellImages = async () => {
      if (!championName) return;
      
      try {
        setSpellImagesLoading(true);
        const championData = await DataService.fetchChampionHeaderData(championName);
        setSpellImages(championData.spells);
      } catch (err) {
        console.error('Error fetching spell images:', err);
      } finally {
        setSpellImagesLoading(false);
      }
    };

    const fetchSkillData = async () => {
      if (!championName) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch skill priority data
        const priorityResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/skillPriority`);
        if (!priorityResponse.ok) {
          throw new Error(`Failed to fetch skill priority data: ${priorityResponse.status}`);
        }
        const priorityData = await priorityResponse.json();
        setSkillPriority(priorityData);
        
      } catch (err) {
        console.error('Error fetching skill data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSkillData();
    fetchSpellImages();
  }, [championName]);
  
  // Helper function to render spell image with key label
  const renderSpellImage = (index: number, keyLabel: string) => {
    if (spellImagesLoading) {
      return <div className="so-spell-loading">{keyLabel}</div>;
    }
    
    if (!spellImages || spellImages.length <= index) {
      return keyLabel;
    }
    
    const spell = spellImages[index];
    return (
      <div className="so-spell-image-container">
        <img 
          src={spell.imagePath} 
          alt={`${championName} ${keyLabel}`} 
          className="so-spell-image" 
        />
        <span className="so-spell-key">{keyLabel}</span>
      </div>
    );
  };
  
  // If loading, show appropriate message
  if (loading) {
    return <div className="so-skill-priority so-loading">Loading skill priority...</div>;
  }
  
  // If error, show error message
  if (error) {
    return <div className="so-skill-priority so-empty">Error loading skill priority</div>;
  }
  
  // If no data, show appropriate message
  if (!skillPriority?.length) {
    return <div className="so-skill-priority so-empty">No skill priority data available</div>;
  }
  
  const topPriority = skillPriority[0];
  
  // Helper function to render priority with images
  const renderPriorityWithImages = () => {
    if (!topPriority) return null;
    
    const primary = topPriority.primary_slot - 1; // Convert to 0-based index
    const secondary = topPriority.secondary_slot - 1;
    const tertiary = topPriority.tertiary_slot - 1;
    
    return (
      <div className="so-skill-priority-with-images">
        {primary >= 0 && (
          <div className="so-priority-spell">
            {renderSpellImage(primary, skillSlotToLabel(primary + 1))}
          </div>
        )}
        {secondary >= 0 && (
          <>
            <span className="so-priority-separator">&gt;</span>
            <div className="so-priority-spell">
              {renderSpellImage(secondary, skillSlotToLabel(secondary + 1))}
            </div>
          </>
        )}
        {tertiary >= 0 && (
          <>
            <span className="so-priority-separator">&gt;</span>
            <div className="so-priority-spell">
              {renderSpellImage(tertiary, skillSlotToLabel(tertiary + 1))}
            </div>
          </>
        )}
      </div>
    );
  };
  
  return (
    <div className="so-skill-priority">
      {topPriority ? renderPriorityWithImages() : "Skill Priority: Unknown"}
    </div>
  );
};

export default ChampionSkillPriority; 